import React, { useCallback, useContext, useEffect, useState } from "react";
import { AppContext } from "../../context/AppContext";
import { PartnerFile } from "../../services/PartnerFileService";
import { CustomerFiles } from "./CustomerFiles";
import { ProvidedFiles, ProvidedGroupView } from "./ProvidedFiles";
import {
  FileExchangeGroup,
  FileQuota,
  formatPhrase,
  getLocale
} from "./shared";

/**
 * the two sides of the module: what the partner receives from its suppliers, and
 * what it provides to its customers
 */
type TabKey = "customer" | "provided";

/**
 * FileExchangeComponent
 * container of the file exchange: it loads both sides and hands each of them to
 * its view. a side that has nothing to show is not offered at all, and with only
 * one side left the tab bar disappears with it
 * @constructor
 */
const FileExchangeComponent: React.FC = () => {
  const {
    I18N,
    services: {
      servicePartner,
      servicePartnerFile
    }
  } = useContext(AppContext);

  const [supplierGroups, setSupplierGroups] = useState<FileExchangeGroup[]>([]);
  const [providedGroups, setProvidedGroups] = useState<ProvidedGroupView[]>([]);
  const [quota, setQuota] = useState<FileQuota | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  /**
   * the two sides fail on their own: one of them being unreachable must not take
   * the other one off the page
   */
  const [hasCustomerError, setHasCustomerError] = useState<boolean>(false);
  const [hasProvidedError, setHasProvidedError] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<TabKey>("customer");
  /**
   * file whose download url is currently being resolved
   */
  const [pendingFileId, setPendingFileId] = useState<string>("");
  const [downloadError, setDownloadError] = useState<string>("");
  /**
   * bumped by the retry button, which is the only way to run the load again
   */
  const [reloadToken, setReloadToken] = useState<number>(0);

  /**
   * locale
   */
  const locale: string = getLocale(I18N);

  /**
   * load both sides in parallel and resolve the partner identities behind them
   */
  useEffect(() => {
    let isCurrent = true;

    /**
     * loadSupplierGroups
     * the files of every supplier that releases something to this partner
     */
    const loadSupplierGroups = async (): Promise<FileExchangeGroup[]> => {
      const fileGroups = await servicePartnerFile.getFiles(),
        supplierIds = Object.keys(fileGroups || {});

      /**
       * name and logo do not come with the file list, they have to be looked
       * up per supplier. one failing lookup only costs that supplier its
       * name, never its files
       */
      const resolved: FileExchangeGroup[] = await Promise.all(
        supplierIds.map(async (supplierId) => {
          let name = supplierId,
            hashId = "";

          try {
            const partner = await servicePartner.getById(supplierId);

            name = partner?.name || supplierId;
            hashId = partner?.hashId || "";
          } catch (error) {
            console.error("Could not resolve supplier " + supplierId, error);
          }

          const files = Array.isArray(fileGroups[supplierId]) ? [...fileGroups[supplierId]] : [];

          // folder before file, and numbered files in human order
          files.sort((a, b) => (a?.frontendPath || "").localeCompare(
            b?.frontendPath || "",
            locale,
            { numeric: true }
          ));

          return { supplierId, name, hashId, files };
        })
      );

      resolved.sort((a, b) => a.name.localeCompare(b.name, locale, {
        sensitivity: "base",
        numeric: true
      }));

      return resolved;
    };

    /**
     * loadProvided
     * the partner's own releases plus its storage consumption
     */
    const loadProvided = async (): Promise<{ quota: FileQuota; groups: ProvidedGroupView[] }> => {
      const provided = await servicePartnerFile.getProvidedFiles();

      const resolved: ProvidedGroupView[] = await Promise.all(
        provided.groups.map(async (group): Promise<ProvidedGroupView> => {
          let customerName = group.customerId || "";

          /**
           * same as on the customer side: a failing lookup only costs the
           * group its customer name, and then the id stands in for it
           */
          if (group.customerId) {
            try {
              const partner = await servicePartner.getById(group.customerId);

              customerName = partner?.name || group.customerId;
            } catch (error) {
              console.error("Could not resolve customer " + group.customerId, error);
            }
          }

          return {
            key: group.visibility + ":" + (group.customerId || ""),
            visibility: group.visibility,
            customerId: group.customerId,
            customerName,
            files: Array.isArray(group.files) ? [...group.files] : []
          };
        })
      );

      // the release to everyone first, the single customers after it by name
      resolved.sort((a, b) => {
        if (a.visibility !== b.visibility) {
          return a.visibility === "public" ? -1 : 1;
        }

        return a.customerName.localeCompare(b.customerName, locale, {
          sensitivity: "base",
          numeric: true
        });
      });

      return { quota: provided.quota, groups: resolved };
    };

    const load = async (): Promise<void> => {
      setIsLoading(true);
      setHasCustomerError(false);
      setHasProvidedError(false);

      const [supplierResult, providedResult] = await Promise.allSettled([
        loadSupplierGroups(),
        loadProvided()
      ]);

      if (!isCurrent) {
        return;
      }

      if (supplierResult.status === "fulfilled") {
        setSupplierGroups(supplierResult.value);
      } else {
        console.error("Could not load partner files", supplierResult.reason);
        setSupplierGroups([]);
        setHasCustomerError(true);
      }

      if (providedResult.status === "fulfilled") {
        setQuota(providedResult.value.quota);
        setProvidedGroups(providedResult.value.groups);
      } else {
        console.error("Could not load provided files", providedResult.reason);
        setQuota(null);
        setProvidedGroups([]);
        setHasProvidedError(true);
      }

      setIsLoaded(true);
      setIsLoading(false);
    };

    load();

    return () => {
      isCurrent = false;
    };
  }, [servicePartnerFile, servicePartner, locale, reloadToken]);

  /**
   * handleRetry
   */
  const handleRetry = useCallback((): void => {
    setReloadToken((current) => current + 1);
  }, []);

  /**
   * handleDownload
   * the url is presigned and short lived, so it is fetched per click. the
   * endpoint answers for the partner's own files as well, which is why both
   * views download through this one handler
   * @param file
   */
  const handleDownload = useCallback(async (file: PartnerFile): Promise<void> => {
    setDownloadError("");
    setPendingFileId(file.fileId);

    try {
      const download = await servicePartnerFile.getDownloadUrl(file.fileId);

      if (!download || !download.url) {
        throw new Error("download url missing");
      }

      /**
       * deliberately not window.open(): the popup blocker kills a tab opened
       * after an await. the url answers with content-disposition attachment,
       * so assigning it starts the download and leaves the page where it is
       */
      window.location.href = download.url;
    } catch (error) {
      console.error("Could not resolve download url", error);
      setDownloadError(formatPhrase(
        I18N.t("Der Download von \"%s\" konnte nicht gestartet werden."),
        [file.filename || file.frontendPath || ""]
      ));
    } finally {
      setPendingFileId("");
    }
  }, [servicePartnerFile, I18N]);

  /**
   * a side is only worth a tab when it carries something: a supplier that
   * released files, or -- for the own side -- storage of its own or files in it
   */
  const hasCustomerView: boolean = !!supplierGroups.length,
    hasProvidedView: boolean = !!quota
      && (quota.max > 0 || providedGroups.some((group) => !!group.files.length)),
    hasTabs: boolean = hasCustomerView && hasProvidedView,
    /**
     * derived instead of corrected after the load: the chosen tab only counts
     * while its side is actually there, and otherwise the remaining side shows
     */
    activeView: TabKey | "" = (activeTab === "provided" && hasProvidedView)
      ? "provided"
      : hasCustomerView
        ? "customer"
        : hasProvidedView
          ? "provided"
          : "";

  /**
   * one message for both loads: which of them failed only changes its wording
   */
  const errorText: string = (hasCustomerError && hasProvidedError)
    ? I18N.t("Die Dateien konnten nicht geladen werden.")
    : hasCustomerError
      ? I18N.t("Die Dateien Ihrer Lieferanten konnten nicht geladen werden.")
      : I18N.t("Ihre bereitgestellten Dateien konnten nicht geladen werden.");

  /**
   * render
   */
  return (
    <div data-file-exchange>
      {/* loading and failure keep separate elements: a role that changes on the
          fly is not reliably announced */}
      <div
        data-file-exchange-status
        role="status"
        aria-live="polite"
      >
        {isLoading && I18N.t("Dateien werden geladen …")}

        {!isLoading && isLoaded && !hasCustomerView && !hasProvidedView
          && !hasCustomerError && !hasProvidedError
          && I18N.t("Für Sie ist derzeit kein Lieferant für den Dateiaustausch freigeschaltet.")}
      </div>

      {!isLoading && (hasCustomerError || hasProvidedError) && (
        <div data-file-exchange-error role="alert">
          {errorText}
          <button
            type="button"
            data-file-exchange-retry
            data-small
            data-secondary-light
            onClick={handleRetry}
          >
            {I18N.t("Erneut versuchen")}
          </button>
        </div>
      )}

      {/* a failed download never touches the list, it only adds this line */}
      {!!downloadError && (
        <div data-file-exchange-download-error role="alert">
          {downloadError}
        </div>
      )}

      {hasTabs && (
        <dia-tabs>
          <input
            type="radio"
            name="tabset"
            id="dia-tab1"
            checked={activeView === "customer"}
            onChange={() => setActiveTab("customer")}
          />
          <label htmlFor="dia-tab1">{I18N.t("Von meinen Lieferanten")}</label>

          <input
            type="radio"
            name="tabset"
            id="dia-tab2"
            checked={activeView === "provided"}
            onChange={() => setActiveTab("provided")}
          />
          <label htmlFor="dia-tab2">{I18N.t("Meine bereitgestellten Dateien")}</label>
        </dia-tabs>
      )}

      {activeView === "customer" && (
        <CustomerFiles
          groups={supplierGroups}
          pendingFileId={pendingFileId}
          onDownload={handleDownload}
        />
      )}

      {activeView === "provided" && !!quota && (
        <ProvidedFiles
          quota={quota}
          groups={providedGroups}
          pendingFileId={pendingFileId}
          onDownload={handleDownload}
        />
      )}
    </div>
  );
};

export default FileExchangeComponent;
